import styled from 'styled-components';

import { Text } from '../../../../components';
import { hoverWash } from '../../../../theme/mixins';
import { ANIM, BAR_COLORS, COLORS, GRADIENTS } from '../../../../theme/tokens';

// The sheet shell (backdrop, panel, portal, close button, header divider, drag-to-dismiss) comes
// from the shared <Sheet> (@/nova/components/Sheet). Everything below is this sheet's own
// per-screen content: the pack picker, the wallet/card settlement screens, success and error.

/** Cross-fade wrapper for the sheet's per-screen body/footer, mirroring the wallet
 *  DepositModal's `.deposit-screen`: fades the outgoing screen out, then the incoming one in,
 *  while the Sheet's `animateResize` grows/shrinks the panel around the swap. */
export const ScreenFade = styled.div<{ $shown: boolean }>`
  width: 100%;
  transition:
    opacity ${ANIM.INTERACT_MS}ms ease,
    transform ${ANIM.INTERACT_MS}ms ease;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transform: translateY(${(p) => (p.$shown ? '0' : '4px')});
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const Intro = styled(Text)`
  display: block;
  font-size: 13px;
  line-height: 1.45;
  color: ${COLORS.textSubtle};
`;

/* ---------- pack picker ---------- */

export const PackGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  width: 100%;
`;

/** Selectable pack card. Same clickable-card pattern as the product tier picker: a theme-aware
 *  grey `surfaceFaint` fill + the shared `hoverWash` highlight, with the WHOLE card acting as
 *  the control. Only the selected card carries the orange ring + glow; unselected keeps a 1px
 *  transparent border so selecting never shifts the layout. */
export const PackCard = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  margin: 0;
  padding: 18px 16px;
  text-align: left;
  font: inherit;
  color: inherit;
  appearance: none;
  border-radius: 16px;
  border: 1px solid ${(p) => (p.$selected ? COLORS.planSelectedBorder : 'transparent')};
  background: ${(p) => (p.$selected ? `${GRADIENTS.planSelectedGlow}, ${COLORS.surfaceFaint}` : COLORS.surfaceFaint)};
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms,
    background ${ANIM.INTERACT_MS}ms,
    opacity ${ANIM.INTERACT_MS}ms;
  ${hoverWash}

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

/** "Best value" marketing pill, shown on the featured pack. Reuses the tier-pill orange
 *  pairing (see the product-detail payment modal). */
export const FeaturedBadge = styled(Text)`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: ${COLORS.tierPillBg};
  color: ${COLORS.ctaPillBorder};
`;

export const PackCredits = styled(Text)<{ $selected: boolean }>`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  color: ${(p) => (p.$selected ? COLORS.planSelectedBorder : COLORS.textBrightest)};
`;

/** Pack price. Preformatted money (a data value), rendered as plain text rather than through
 *  `<Text>`/translation. */
export const PackPrice = styled.div`
  font-size: 13px;
  color: ${COLORS.textSubtle};
`;

/* Custom amount row — a numeric credit input with the unit + derived money cost, mirroring the
   header CreditsModal's custom row. */
export const CustomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const AmountInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  &::placeholder {
    color: ${COLORS.textFaint};
    font-weight: 400;
  }
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
  }
  &:disabled {
    opacity: 0.6;
  }
`;

export const CustomUnit = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/** The derived money cost of the chosen amount ("$25.00 USD"), right-aligned beside the input. */
export const CostHint = styled.div`
  font-size: 12px;
  color: ${COLORS.textMuted};
  font-variant-numeric: tabular-nums;
  min-width: 84px;
  text-align: right;
`;

/** A subtle funding note (MAIN can't cover this / need a USD wallet), under the amount row. */
export const FundingNote = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: ${COLORS.textMuted};
`;

/** Orange check badge in the top-right corner of the selected pack card, matching the product
 *  tier picker's selection marker. Absolutely positioned so it never affects the card's layout. */
export const SelectedCheck = styled.span`
  position: absolute;
  top: 14px;
  right: 14px;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 99px;
  background: ${COLORS.planSelectedBorder};
`;

export const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 16px;
  border-radius: 16px;
  background: ${COLORS.surfaceFaint};
`;

/* ---------- purchase summary (paying / form screens) ---------- */

export const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
`;

export const SummaryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const SummaryLabel = styled(Text)`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${COLORS.textSubtle};
`;

export const SummaryAmount = styled(Text)`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: ${COLORS.textBrightest};
`;

/** Preformatted money (a data value), rendered as plain text. */
export const SummaryPrice = styled.div`
  font-size: 13px;
  color: ${COLORS.textSubtle};
`;

/** Underlined text-button ("Change pack" / "Pay by card instead"). */
export const TextLink = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  text-decoration: underline;
  color: ${COLORS.accent};
  cursor: pointer;

  &:disabled {
    color: ${COLORS.textMuted};
    text-decoration: none;
    cursor: default;
  }
`;

export const StatusText = styled(Text)`
  display: block;
  font-size: 13px;
  color: ${COLORS.textSubtle};
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

/** An already-resolved error string (a data value), rendered as plain text. */
export const ErrorText = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${BAR_COLORS.danger};
`;

/* ---------- success screen ---------- */

export const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  width: 100%;
  padding: 16px 8px 8px;
`;

export const SuccessIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${COLORS.badgeGreenBg};
`;

export const SuccessTitle = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.textBrightest};
`;

export const SuccessBody = styled(Text)`
  font-size: 13px;
  line-height: 1.45;
  color: ${COLORS.textSubtle};
`;

export const SuccessNote = styled(Text)`
  max-width: 320px;
  font-size: 12px;
  line-height: 1.45;
  color: ${COLORS.textFaint};
`;

/* ---------- error screen ---------- */

export const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  width: 100%;
  padding: 16px 8px 8px;
`;

/** An already-resolved error string (a data value), rendered as plain text. */
export const ErrorMessage = styled.span`
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.textSubtle};
`;
